/**
 * Kelly's header launcher: an "Ask Kelly" pill that sits immediately after the theme switcher in every top bar.
 * It reaches the shells the nav rail does not, so Kelly is openable on any authenticated page without depending
 * on the rail work.
 */

import { useCallback, useRef } from "react"
import { AutoAwesome } from "@mui/icons-material"
import { Button } from "@mui/material"
import type { SxProps, Theme } from "@mui/material"
import Tooltip from "components/material/tooltip"
import { DOCK_OPENER_ATTRIBUTES, useRememberDockOpener } from "../chatbox/dock-opener"
import { useKellyDockToggle } from "../chatbox/use-dock-toggle"

const LAUNCHER_SX = {
  ml: 1,
  flexShrink: 0,
  whiteSpace: "nowrap",
} satisfies SxProps<Theme>

export function KellyLauncherButton() {
  const { isAvailable, isOpen, toggle } = useKellyDockToggle()
  const rememberOpener = useRememberDockOpener()
  const buttonRef = useRef<HTMLButtonElement>(null)

  const onClick = useCallback(() => {
    // Only when this click OPENS the dock: recording on a close banks a control nobody will be returning from.
    if (!isOpen) rememberOpener(buttonRef.current)
    toggle()
  }, [isOpen, rememberOpener, toggle])

  if (!isAvailable) return null

  return (
    <Tooltip title={isOpen ? "Hide Kelly" : "Ask Kelly"}>
      {/* `contained` is load-bearing, not cosmetic: the theme forces `color: inherit` on every
          `:not(.MuiButton-contained)` button, so an outlined pill would inherit the AppBar's white. */}
      <Button
        ref={buttonRef}
        color="primary"
        variant="contained"
        size="small"
        startIcon={<AutoAwesome fontSize="small" />}
        sx={LAUNCHER_SX}
        aria-label="Ask Kelly"
        aria-expanded={isOpen}
        data-testid="kelly-header-launcher"
        {...DOCK_OPENER_ATTRIBUTES}
        onClick={onClick}
      >
        Ask Kelly
      </Button>
    </Tooltip>
  )
}
